import { useState } from 'react';

import {
  createStandaloneCharge,
  StandaloneChargePaymentMethodEnum
} from '../../../services/standalone-charges';
import { dangerNotification } from '../../../services/notification';
import { isValidEmail } from '../../../utils/emailValidator';
import { maskCpf, maskCnpj, unmaskCpfOrCnpj, unmaskPhoneNumber, maskZipCode } from '../../../services/masks';
import { isCnpj, isCpf } from '../../../shared/documents';
import { AddressService } from '../../../services/address';

import { CreateFormErrors, CreateFormState } from '../types';
import { initialCreateForm } from '../constants';

type Params = {
  isEstateUser: boolean;
  defaultEstateId: string;
  isCreateBlocked: boolean;
  getCreateBlockMessage: () => string;
  onCreated: (chargeLinkUrl: string, paymentMethod: 'PIX' | 'BOLETO') => void;
};

const boletoAddressFields: Array<keyof CreateFormState> = [
  'payerAddressStreet',
  'payerAddressNumber',
  'payerAddressNeighborhood',
  'payerAddressZipCode',
  'payerAddressCity',
  'payerAddressState'
];

export const useCreateStandaloneChargeForm = ({
  isEstateUser,
  defaultEstateId,
  isCreateBlocked,
  getCreateBlockMessage,
  onCreated
}: Params) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(initialCreateForm);
  const [createFormErrors, setCreateFormErrors] = useState<CreateFormErrors>({});
  const [savingCreate, setSavingCreate] = useState(false);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressAutoFilled, setAddressAutoFilled] = useState<string[]>([]);

  const resetCreateForm = (prefill?: Partial<CreateFormState>) => {
    setCreateForm({
      ...initialCreateForm,
      ...prefill,
      estateId: isEstateUser ? '' : prefill?.estateId || defaultEstateId || '',
      commissionCollaboratorIds: []
    });
    setCreateFormErrors({});
    setAddressLookupError(null);
    setAddressAutoFilled([]);
  };

  const focusCreateField = (field: keyof CreateFormState) => {
    const focusMap: Partial<Record<keyof CreateFormState, () => void>> = {
      estateId: () => document.getElementById('create-estate-dropdown')?.focus(),
      amount: () => document.getElementById('create-amount-input')?.focus(),
      payerName: () => document.getElementById('create-payer-name-input')?.focus(),
      payerCpf: () => document.getElementById('create-payer-cpf-input')?.focus(),
      payerEmail: () => document.getElementById('create-payer-email-input')?.focus(),
      payerPhone: () => document.getElementById('create-payer-phone-input')?.focus(),
      payerAddressStreet: () => document.getElementById('create-payer-address-street-input')?.focus(),
      payerAddressNumber: () => document.getElementById('create-payer-address-number-input')?.focus(),
      payerAddressNeighborhood: () =>
        document.getElementById('create-payer-address-neighborhood-input')?.focus(),
      payerAddressComplement: () =>
        document.getElementById('create-payer-address-complement-input')?.focus(),
      payerAddressZipCode: () =>
        document.getElementById('create-payer-address-zip-code-input')?.focus(),
      payerAddressCity: () => document.getElementById('create-payer-address-city-input')?.focus(),
      payerAddressState: () => document.getElementById('create-payer-address-state-input')?.focus(),
      payerAddressCountry: () =>
        document.getElementById('create-payer-address-country-input')?.focus(),
      description: () => document.getElementById('create-description-input')?.focus(),
      paymentMethod: () => document.getElementById('create-payment-method-input')?.focus()
    };
    focusMap[field]?.();
  };

  const getCreateFormErrors = (form: CreateFormState): CreateFormErrors => {
    const nextErrors: CreateFormErrors = {};
    if (!form.amount || form.amount <= 0) {
      nextErrors.amount = 'Informe um valor válido maior que zero.';
    }
    if (!form.payerName.trim()) {
      nextErrors.payerName = 'Informe o nome completo do pagador.';
    }
    if (!form.payerCpf) {
      nextErrors.payerCpf =
        form.payerType === 'company'
          ? 'Informe um CNPJ válido com 14 caracteres.'
          : 'Informe um CPF válido com 11 dígitos.';
    } else {
      const document = unmaskCpfOrCnpj(form.payerCpf);
      if (form.payerType === 'company') {
        if (document.length !== 14 || !isCnpj(document)) {
          nextErrors.payerCpf = 'Informe um CNPJ válido com 14 caracteres.';
        }
      } else if (document.length !== 11 || !isCpf(document)) {
        nextErrors.payerCpf = 'Informe um CPF válido com 11 dígitos.';
      }
    }
    if (!isValidEmail(form.payerEmail)) {
      nextErrors.payerEmail = 'Informe um e-mail válido.';
    }
    if (!form.payerPhone || unmaskPhoneNumber(form.payerPhone).length < 10) {
      nextErrors.payerPhone = 'Informe um telefone válido com DDD.';
    }
    if (!form.description.trim()) {
      nextErrors.description = 'Descreva o motivo da cobrança.';
    }
    if (!isEstateUser && !form.estateId) {
      nextErrors.estateId = 'Selecione a imobiliária para esta cobrança.';
    }
    if (!form.paymentMethod) {
      nextErrors.paymentMethod = 'Selecione o método de pagamento.';
    }
    if (form.paymentMethod === StandaloneChargePaymentMethodEnum.boleto) {
      if (!form.payerAddressStreet.trim()) {
        nextErrors.payerAddressStreet = 'Informe a rua do pagador.';
      }
      if (!form.payerAddressNumber.trim()) {
        nextErrors.payerAddressNumber = 'Informe o número do endereço.';
      }
      if (!form.payerAddressNeighborhood.trim()) {
        nextErrors.payerAddressNeighborhood = 'Informe o bairro do pagador.';
      }
      if (form.payerAddressZipCode.replace(/\D/g, '').length !== 8) {
        nextErrors.payerAddressZipCode = 'Informe um CEP válido com 8 dígitos.';
      }
      if (!form.payerAddressCity.trim()) {
        nextErrors.payerAddressCity = 'Informe a cidade do pagador.';
      }
      if (form.payerAddressState.trim().length !== 2) {
        nextErrors.payerAddressState = 'Informe a UF com 2 letras.';
      }
    }
    return nextErrors;
  };

  const updateCreateFormField = <K extends keyof CreateFormState>(field: K, value: CreateFormState[K]) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'estateId' ? { commissionCollaboratorIds: [] } : {}),
      ...(field === 'payerType' ? { payerCpf: '' } : {})
    }));
    if (field === 'paymentMethod' && value !== StandaloneChargePaymentMethodEnum.boleto) {
      setAddressLookupError(null);
      setAddressAutoFilled([]);
    }
    setCreateFormErrors(prev => {
      if (field === 'paymentMethod' && value !== StandaloneChargePaymentMethodEnum.boleto) {
        const rest = { ...prev };
        delete rest[field];
        boletoAddressFields.forEach(addressField => delete rest[addressField]);
        return rest;
      }
      if (!prev[field]) return prev;
      const rest = { ...prev };
      delete rest[field];
      return rest;
    });
  };

  const handleCreateZipCodeLookup = async (zipCodeRaw: string) => {
    const cleanZip = zipCodeRaw.replace(/\D/g, '');
    if (cleanZip.length !== 8) return;

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    setAddressAutoFilled([]);
    try {
      const response = (await AddressService.getAddressByZipCode(cleanZip)) as {
        street?: string;
        district?: string;
        city?: string;
        state?: string;
        zip_code?: string;
      };
      const updates: Partial<CreateFormState> = {};
      const filled: string[] = [];
      if (response.street) {
        updates.payerAddressStreet = response.street;
        filled.push('payerAddressStreet');
      }
      if (response.district) {
        updates.payerAddressNeighborhood = response.district;
        filled.push('payerAddressNeighborhood');
      }
      if (response.city) {
        updates.payerAddressCity = response.city;
        filled.push('payerAddressCity');
      }
      if (response.state) {
        updates.payerAddressState = String(response.state).trim().toUpperCase();
        filled.push('payerAddressState');
      }
      setCreateForm(prev => ({ ...prev, ...updates }));
      setAddressAutoFilled(filled);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e || '');
      setAddressLookupError(msg || 'CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const onPayerAddressZipCodeInput = (value: string) => {
    const masked = maskZipCode(value);
    updateCreateFormField('payerAddressZipCode', masked);
    const digits = masked.replace(/\D/g, '');
    if (digits.length < 8) {
      setAddressLookupError(null);
      setAddressAutoFilled([]);
    }
    if (digits.length === 8) {
      void handleCreateZipCodeLookup(masked);
    }
  };

  const openCreateModal = (prefill?: Partial<CreateFormState>) => {
    if (isCreateBlocked) {
      dangerNotification('Emissão bloqueada', getCreateBlockMessage());
      return;
    }
    resetCreateForm(prefill);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => setCreateModalOpen(false);

  const validateCreateForm = () => {
    const formErrors = getCreateFormErrors(createForm);
    setCreateFormErrors(formErrors);
    const firstInvalidField = (Object.keys(formErrors) as Array<keyof CreateFormState>)[0];

    if (firstInvalidField) {
      dangerNotification('Validação', 'Revise os campos obrigatórios destacados no formulário.');
      focusCreateField(firstInvalidField);
      return false;
    }

    return true;
  };

  const handleCreateCharge = async () => {
    if (!validateCreateForm()) return;

    setSavingCreate(true);
    try {
      const isBoleto = createForm.paymentMethod === StandaloneChargePaymentMethodEnum.boleto;
      const created = await createStandaloneCharge({
        estateId: isEstateUser ? undefined : createForm.estateId,
        commissionCollaboratorIds: createForm.commissionCollaboratorIds,
        amount: Number(createForm.amount.toFixed(2)),
        payerType: createForm.payerType,
        payerName: createForm.payerName.trim(),
        payerCpf: unmaskCpfOrCnpj(createForm.payerCpf),
        payerEmail: createForm.payerEmail.trim().toLowerCase(),
        payerPhone: unmaskPhoneNumber(createForm.payerPhone),
        ...(isBoleto
          ? {
              payerAddressStreet: createForm.payerAddressStreet.trim(),
              payerAddressNumber: createForm.payerAddressNumber.trim(),
              payerAddressNeighborhood: createForm.payerAddressNeighborhood.trim(),
              payerAddressComplement: createForm.payerAddressComplement.trim() || undefined,
              payerAddressZipCode: createForm.payerAddressZipCode.replace(/\D/g, ''),
              payerAddressCity: createForm.payerAddressCity.trim(),
              payerAddressState: createForm.payerAddressState.trim().toUpperCase(),
              payerAddressCountry: createForm.payerAddressCountry.trim().toUpperCase() || 'BR'
            }
          : {}),
        description: createForm.description.trim(),
        paymentMethod: createForm.paymentMethod as StandaloneChargePaymentMethodEnum
      });

      setCreateModalOpen(false);
      setCreateFormErrors({});
      onCreated(created.chargeLinkUrl, created.paymentMethod);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      dangerNotification('Erro', message || 'Não foi possível criar cobrança.');
    } finally {
      setSavingCreate(false);
    }
  };

  return {
    createModalOpen,
    openCreateModal,
    closeCreateModal,
    createForm,
    createFormErrors,
    savingCreate,
    updateCreateFormField,
    validateCreateForm,
    handleCreateCharge,
    resetCreateForm,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    onPayerAddressZipCodeInput
  };
};
